import {isEmpty, isNumber} from 'lodash-es';
import Papa from 'papaparse';
import {useCallback, useState} from 'react';

import {removeEmptyRows} from '@/constants/formatters';
import {fileService, rateBucketKey} from '@/services/fileService';
import {ratesService} from '@/services/ratesService';
import type {
  AfriexCurrencyCode,
  AfriexOTCRate,
  ArbitrageCheckInfo,
  UserType,
} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {calculateArbitrageProfit} from '@/utils/rates/calculateArbitrageProfit';
import {cleanNumber} from '@/utils/rates/cleanNumber';
import {detectArbitrage} from '@/utils/rates/detectArbitrage';
import {removeUndefinedKeys} from '@/utils/rates/removeUndefinedKeys';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

type OtcCsvFormat = {
  Base_Currency: AfriexCurrencyCode;
  Quote_Currency: AfriexCurrencyCode;
  OTC_Rate: number;
  Rate_Source: string;
  Margin: number;
  App_Rate: number;
};

/**
 * Extract currency codes from rates object
 */
const extractSymbols = (rows: readonly Partial<OtcCsvFormat>[]) => {
  const symbols = [
    ...new Set(rows.map((item) => item.Base_Currency)),
  ] as AfriexCurrencyCode[];
  return symbols;
};

// What `mapMatrixToRatesMap` actually returns: a rates map keyed by
// currency code with NUMBER leaves. `RatesMap`/`RatesValueMap`
// (src/types/RatesMap.ts, src/types/RatesValueMap.ts) describe the wire
// shape of rates fetched from the API, which are STRING leaves, and are used
// that way by other screens (Rates/App, Rates/Promo, AfriexAdminTransaction,
// AfriexBusinessTransaction) -- not to be changed here. This local matrix ->
// map conversion never touches the wire; it stays number-leaved end to end.
export type AppRatesNumberMap = Partial<
  Record<AfriexCurrencyCode, Partial<Record<AfriexCurrencyCode, number>>>
>;

/**
 * The arbitrage checker works with data in matrix form (array of array).
 * This function converts back to rates object
 */
const mapMatrixToRatesMap = (
  matrix: number[][],
  currencies: AfriexCurrencyCode[],
): AppRatesNumberMap => {
  const ratesMap: AppRatesNumberMap = {};
  for (let i = 0; i < matrix.length; i += 1) {
    const fromSymbol = currencies[i];
    const row = matrix[i];
    const toRates: Partial<Record<AfriexCurrencyCode, number>> = {};
    for (let j = 0; j < row.length; j += 1) {
      const toSymbol = currencies[j];
      toRates[toSymbol] = row[j];
    }
    ratesMap[fromSymbol] = toRates;
  }
  return ratesMap;
};

const getMaxProfit = (arbitrageResult: ArbitrageCheckInfo) => {
  if (!arbitrageResult) {
    return 0;
  }
  const maxCycleProfit = Math.max(
    ...(arbitrageResult?.cycles?.map((cycle) =>
      calculateArbitrageProfit(cycle),
    ) ?? []),
  );
  return Math.max(
    arbitrageResult?.pairWiseDiscrepancy?.maxProfit || 0,
    maxCycleProfit,
  );
};

const useRatesUpdate = (target: UserType = 'AFRIEX') => {
  const toleranceMax = 1.11;
  const defaultTolerance = 1.1;
  const [appRates, setAppRates] = useState<number[][]>([]);
  const [loadingText, setLoadingText] = useState<string>('');
  const [isArbitrageChecked, setIsArbitrageChecked] = useState<boolean>(false);
  const [arbitrageResult, setArbitrageResult] = useState<ArbitrageCheckInfo>();
  const [tolerance, setTolerance] = useState<number>(defaultTolerance);
  const [ratesFile, setRatesFile] = useState<Blob>();
  const [isArbitrageDetected, setIsArbitrageDetected] =
    useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currencySymbols, setCurrencySymbols] = useState<AfriexCurrencyCode[]>(
    [],
  );

  // ----- OTC Rates States ---
  const [otcRates, setOTCRates] = useState<AfriexOTCRate[]>([]);

  const handleRatesUpdate = useCallback(
    async (
      appRatesForUpload: AppRatesNumberMap,
      otcRatesForUpload: AfriexOTCRate[],
    ) => {
      setIsLoading(true);
      try {
        if (Object.values(appRatesForUpload).length === 0) {
          return showErrorMessage(
            'Rate is empty, make sure the template matches',
          );
        }

        if (Object.values(otcRatesForUpload).length === 0) {
          return showErrorMessage(
            'OTC Rate is empty, make sure the template matches',
          );
        }

        if (!isArbitrageChecked) {
          return showErrorMessage('Check for arbitrage first');
        }

        if (tolerance >= toleranceMax) {
          return showErrorMessage(
            `Tolerance must be less than ${toleranceMax}`,
          );
        }

        setLoadingText('Saving rates...');

        await fileService.uploadFile(
          (ratesFile as File)?.name,
          rateBucketKey,
          null,
          ratesFile,
        );

        const payload = {
          appRates: removeUndefinedKeys(appRatesForUpload),
          otcRates: removeUndefinedKeys(otcRatesForUpload),
          tolerance,
          ratesLink: (ratesFile as File)?.name,
          arbitrageInfo: arbitrageResult,
          target,
        };
        await ratesService.updateRates(payload);

        setLoadingText('');
        setTolerance(defaultTolerance);
        setArbitrageResult(undefined);
        setIsArbitrageChecked(false);
        setIsArbitrageDetected(false);
        showSuccessMessage('Rates saved successfully');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (e) {
        setLoadingText('');
        showErrorMessage(`Save failed:${getErrorMessage(e, String(e))}`);
      } finally {
        setIsLoading(false);
        setIsConfirmModalOpen(false);
      }
    },
    [isArbitrageChecked, tolerance, ratesFile, arbitrageResult],
  );

  const handleRatesCancellation = useCallback(() => {
    setAppRates([]);
  }, []);

  // No bare `throw` in this body, so this drops `async` and instead wraps
  // every return path in `Promise.resolve()`. Both call sites
  // (`formatCSVToAppRate` below and `useTierUpdate.checkIfRateTierHasArbitrage`
  // in a sibling file) `await` this to sequence work after it; wrapping the
  // returns keeps a genuine `Promise<void>` for them to await, so neither
  // caller's `await-thenable` compliance nor its own (only) `await` is
  // affected. `detectArbitrage` below still runs synchronously inside this
  // function; if it throws, the throw surfaces while the caller's own async
  // body is still executing synchronously (pre-suspension), so the caller's
  // async machinery converts it to a rejection exactly as before, since both
  // call sites only ever invoke this via `await` from an async function.
  const handleArbitrageCheck = (
    ratesMatrix: number[][],
    currencySymbols: AfriexCurrencyCode[],
  ): Promise<void> => {
    if (isEmpty(ratesMatrix)) {
      showErrorMessage('No rates uploaded yet');
    }
    setLoadingText('Checking for arbitrage...');
    setIsArbitrageChecked(false);
    setArbitrageResult({});
    const checkResults = detectArbitrage(
      ratesMatrix,
      currencySymbols,
      tolerance,
    );
    setIsArbitrageChecked(true);
    setLoadingText('');
    const hasNoArbitrage =
      (!checkResults?.cycles || checkResults?.cycles?.length === 0) &&
      (!checkResults?.pairWiseDiscrepancy?.remarks ||
        checkResults?.pairWiseDiscrepancy?.remarks?.length === 0);
    if (hasNoArbitrage) {
      setIsArbitrageDetected(false);
      showSuccessMessage(`No arbitrage detected`);
      setLoadingText('');
      return Promise.resolve();
    }
    const maxProfit = getMaxProfit(checkResults);
    setArbitrageResult({...checkResults, hasArbitrage: true, maxProfit});
    setIsArbitrageDetected(true);
    showErrorMessage(`Arbitrage detected`);
    return Promise.resolve();
  };

  const handleToleranceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const toleranceValue = parseFloat(event.target.value);
      if (!toleranceValue || !isNumber(toleranceValue)) {
        return;
      }
      setTolerance(toleranceValue);
      const profitPercentage = arbitrageResult?.maxProfit || 0;
      if (profitPercentage > toleranceValue) {
        setIsArbitrageDetected(true);
        setArbitrageResult({...(arbitrageResult ?? {}), hasArbitrage: true});
      } else {
        setIsArbitrageDetected(false);
        setArbitrageResult({...(arbitrageResult ?? {}), hasArbitrage: false});
      }
    },
    [arbitrageResult],
  );

  const formatCSVToAppRate = async (
    csvRows: Record<string, Record<string, unknown>>[],
  ) => {
    if (!Array.isArray(csvRows) || csvRows.length === 0) {
      return showErrorMessage('Upload a valid rates csv file to view rates');
    }
    const ratesSymbols = extractSymbols(Object.values(csvRows));
    const size = ratesSymbols?.length;
    const ratesMatrix: number[][] = new Array<number[]>(size)
      .fill([])
      .map(() => new Array<number>(size).fill(0));

    for (let i = 0; i < csvRows.length; i += 1) {
      const currentRow: Record<string, unknown> = csvRows[i];
      if (!currentRow) {
        continue;
      }

      const baseCurrency = currentRow.Base_Currency as AfriexCurrencyCode;
      const quoteCurrency = currentRow.Quote_Currency as AfriexCurrencyCode;
      const appRate = currentRow.App_Rate;

      // Skip if any required field is missing
      if (!baseCurrency || !quoteCurrency || !appRate) {
        continue;
      }

      // Find indices in the ratesSymbols array
      const baseIndex = ratesSymbols.indexOf(baseCurrency);
      const quoteIndex = ratesSymbols.indexOf(quoteCurrency);

      if (baseIndex !== -1 && quoteIndex !== -1) {
        ratesMatrix[baseIndex][quoteIndex] = Number(appRate ?? 0);
      }
    }

    if (!isEmpty(ratesMatrix)) {
      setAppRates(ratesMatrix);
      setCurrencySymbols(ratesSymbols);
      // Awaited so a throw inside the arbitrage check reaches this function's
      // caller (which reports it) instead of becoming an unhandled rejection.
      // `handleArbitrageCheck` contains no `await`, so it already ran to
      // completion before this point; the await only orders the CSV build
      // after it, which is the order the code already had.
      await handleArbitrageCheck(ratesMatrix, ratesSymbols);
      generateAppRatesCsv(ratesMatrix, ratesSymbols);
    }
  };

  const generateAppRatesCsv = (
    ratesMatrix: number[][],
    ratesSymbols: AfriexCurrencyCode[],
  ) => {
    const csvRows: string[] = [];

    // Header row
    csvRows.push(['', ...ratesSymbols].join(','));

    // Data rows
    ratesSymbols.forEach((baseCurrency, i) => {
      const row = [baseCurrency, ...ratesMatrix[i]];
      csvRows.push(row.join(','));
    });

    const csvString = csvRows.join('\n');
    const fileName = `rates-${new Date().toISOString()}.csv`;

    // Create File (not Blob)
    const file = new File([csvString], fileName, {type: 'text/csv'});
    setRatesFile(file);
    return file;
  };

  const handleTemplateDownload = useCallback(async () => {
    const signedUrl = await fileService.fetchUploadedFile(
      'default-otc-rates.csv',
    );
    window.open(signedUrl, '_parent');
  }, []);

  // ----- OTC Rates Functionality ------
  const formatCSVToOTCRate = useCallback((csvRows: Partial<OtcCsvFormat>[]) => {
    if (!Array.isArray(csvRows) || csvRows.length === 0) {
      showErrorMessage('Upload a valid OTC rates csv file to view rates');
      return;
    }

    const rates = csvRows.map((row) => ({
      fromSymbol: row?.['Base_Currency']?.trim().toUpperCase(),
      toSymbol: row?.['Quote_Currency']?.trim().toUpperCase(),
      // `cleanNumber` is declared `(str: string)` but Papa's `dynamicTyping`
      // hands back a number for this column. It short-circuits on numeric
      // input, so the runtime path is unchanged; correcting the declaration
      // belongs with `utils/rates/cleanNumber.ts`.
      value: cleanNumber(row?.['OTC_Rate'] as unknown as string),
      source: row?.['Rate_Source']?.trim()?.toLowerCase() || 'afriex',
    }));
    const definedOTCRates = removeEmptyRows(rates as AfriexOTCRate[]);
    setOTCRates(definedOTCRates);
  }, []);

  const handleRatesUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      try {
        if (event.target.files) {
          setLoadingText('Processing rates upload...');
          const file = event.target.files[0];
          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: (results) => {
              if (results.data) {
                // Papa calls `complete` asynchronously, so it runs outside the
                // try/catch above and neither of these had any handler at all.
                // A malformed CSV that throws in here used to leave the screen
                // on "Processing rates upload..." with the failure only in the
                // console; these are the rates that get uploaded, so the admin
                // has to be told.
                formatCSVToAppRate(
                  results.data as Record<string, Record<string, unknown>>[],
                ).catch((e: unknown) =>
                  showErrorMessage(
                    `Reading app rates failed: ${getErrorMessage(e, String(e))}`,
                  ),
                );
                // formatCSVToOTCRate is synchronous now, so a throw reaches
                // here directly instead of via a promise rejection.
                try {
                  formatCSVToOTCRate(results.data as Partial<OtcCsvFormat>[]);
                } catch (e: unknown) {
                  showErrorMessage(
                    `Reading OTC rates failed: ${getErrorMessage(e, String(e))}`,
                  );
                }
              }
            },
          });
        }
      } catch (_e) {
        showErrorMessage('Upload a valid OTC rates csv file to view rates');
      }
    },
    [formatCSVToOTCRate],
  );

  return {
    handleTemplateDownload,
    handleRatesUpload,
    handleRatesCancellation,
    handleRatesUpdate,
    handleArbitrageCheck,
    appRates: mapMatrixToRatesMap(appRates, currencySymbols),
    loadingText,
    arbitrageResult,
    isArbitrageDetected,
    isArbitrageChecked,
    handleToleranceChange,
    tolerance,
    isLoading,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    currencySymbols,

    // --- OTC ---
    otcRates,
  };
};

export default useRatesUpdate;
